import { useMemo } from 'react';

import {
    useSearchPlayerBonuses,
    useGetBonusTemplateDetails,
    useSearchPlayerAvailableBonusTemplates
} from 'server';

export const useBonusPublicAPI = () => {
    const searchPlayerBonuses = useSearchPlayerBonuses();
    const getBonusTemplateDetails = useGetBonusTemplateDetails();
    const searchPlayerAvailableBonusTemplates = useSearchPlayerAvailableBonusTemplates();

    return useMemo(() => ({
        /**
         *
         * @memberOf Playtech.API.bonus
         * @function
         * @param {Object} options
         * @param {string} options.conversionRate
         * @param {string} options.minimumConversion
         * @returns {Promise<{ bonusTemplates: bonusTemplates[] }>} For bonusTemplates see [ims docs]{@link https://integration.playtech.com/apis/openapi/services/BonusService/types/BonusTemplateDetails.html}
         * @example
         * Playtech.API.bonus.getBonusTemplateDetails({
         *  returnGamingContexts: true,
         *  returnWageringCoefficients: false,
         *  bonusTemplateCodes:[53759]
         * }).then(() => {...});
         */
        getBonusTemplateDetails,
        /**
         *
         * @memberOf Playtech.API.bonus
         * @function
         * @param {Object} options For full acceptable parameters see [ims docs]{@link https://integration.playtech.com/apis/openapi/services/BonusService/operations/SearchPlayerAvailableBonusTemplates.html}
         * @param {string} options.templateCode
         * @param {string} options.templateName
         * @param {string[]} options.triggerTypes
         * @param {string[]} options.playTypes
         * @param {string[]} options.statuses
         * @param {string[]} options.schedulingStatuses
         * @param {string} options.eventName
         * @param {string[]} options.product
         * @param {boolean} options.returnTotalCount
         * @param {number} options.pageSize
         * @param {string} options.pageId
         * @param {boolean} options.returnAllPageIds
         * @returns {Promise<{ nextPageId: string, totalCount: number, pageIds: string[], matchingBonusTemplates: matchingBonusTemplates[] }>} For matchingBonusTemplates see [ims docs]{@link https://integration.playtech.com/apis/openapi/services/BonusService/types/MatchingBonusTemplate.html}
         * @example
         * Playtech.API.bonus.searchPlayerAvailableBonusTemplates({
         *   'triggerTypes' : [ 'deposit', 'promotioncode', 'signup' ],
         *   'promotionCode' : 'TEST',
         *   'depositMethod' : 'Visa',
         *   'depositAmount' : '1.1',
         *   'eturnTotalCount' : true,
         *   'returnAllPageIds' : true,
         *   'pageSize' : 50,
         *   'pageId' : '1',
         * }).then(() => {...});
         */
        searchPlayerAvailableBonusTemplates,
        /**
         *
         * @memberOf Playtech.API.bonus
         * @function
         * @param {Object} options
         * @param {Object} options.playerBonusCriteria
         * @param {string} [options.playerBonusCriteria.issuedAfterDate]
         * @param {string} [options.playerBonusCriteria.issuedBeforeDate]
         * @param {string} [options.playerBonusCriteria.statusFilter] Values: 'current', 'closed', 'redeemed', 'cancelled', 'declined'.
         * @param {string} options.bonusTemplateCriteria
         * @param {string} [options.bonusTemplateCriteria.templateCode]
         * @param {string} [options.bonusTemplateCriteria.templateName]
         * @param {string[]} [options.bonusTemplateCriteria.triggerTypes]
         * @param {string[]} options.bonusTemplateCriteria.playTypes
         * @param {string[]} [options.bonusTemplateCriteria.statuses]
         * @param {string[]} [options.bonusTemplateCriteria.schedulingStatuses]
         * @param {string[]} [options.bonusTemplateCriteria.product]
         * @param {string} [options.bonusTemplateCriteria.eventName]
         * @param {Object} [options.paging]
         * @param {boolean} [options.paging.returnTotalCount]
         * @param {string} options.paging.pageSize
         * @param {string} options.paging.pageId
         * @param {boolean} [options.paging.returnAllPageIds]
         * @param {number} [options.errorLevel]
         * @returns {Promise<{ paging: { nextPageId: string, totalCount: number, pageIds: string[] }, bonuses: bonuses[] }>} For bonuses see [ims docs]{@link https://integration.playtech.com/apis/openapi/services/BonusService/types/MatchingBonus.html}
         * @example
         * Playtech.API.bonus.searchPlayerBonuses().then(() => {...});
         */
        searchPlayerBonuses
    }), [
        searchPlayerBonuses,
        getBonusTemplateDetails,
        searchPlayerAvailableBonusTemplates
    ]);
};
